import React from 'react'
import { useForm } from 'react-hook-form'

import { buildActionAnalyticsAttributes } from '@mondough/analytics-v2'
import { RadioButton, Text, Title } from '@mondough/monzo-ui'

import { Button, PlainLayout } from '../../components'
import { useFormatMessage } from '../../hooks'
import {
  COOKIES_ACCEPTED,
  getCookie,
  setCookieBannerDismissed,
  setCookieConsent,
} from '../../utils/'
import styles from './index.module.scss'

type FormValues = {
  cookiesAccepted: string
}

export default function CookiesPage() {
  const [prefsUpdated, setPrefsUpdated] = React.useState(false)
  const formatMessage = useFormatMessage()
  const cookieValue = getCookie(COOKIES_ACCEPTED) ?? 'false'
  const { handleSubmit, register } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      cookiesAccepted: cookieValue,
    },
  })

  return (
    <PlainLayout
      pageTitle={formatMessage('cookies.page.title')}
      className={styles.container}
    >
      {formatMessage('cookies.card.intro')
        .split('\n')
        .map((p, i) => (
          <Text key={i} marginBottom="small">
            {p}
          </Text>
        ))}
      <form
        onSubmit={handleSubmit(async ({ cookiesAccepted }) => {
          await setCookieConsent(cookiesAccepted)
          setCookieBannerDismissed()
          setPrefsUpdated(true)
        })}
      >
        <Title is="h2">{formatMessage('cookies.card.settings.title')}</Title>
        <ul>
          <li>
            <Title is="h3" size="extra-small" marginBottom="small">
              {formatMessage('cookies.card.settings.default.title')}
            </Title>
            <Text>
              {formatMessage('cookies.card.settings.default.description')}
            </Text>
          </li>
          <li>
            <Title is="h3" size="extra-small" marginBottom="small">
              {formatMessage('cookies.card.settings.analytics.title')}
            </Title>
            <Text marginBottom="none">
              {formatMessage('cookies.card.settings.analytics.description')}
            </Text>
          </li>
        </ul>

        <fieldset className={styles.buttons}>
          <legend>{formatMessage('cookies.card.settings.form.legend')}</legend>
          <RadioButton
            label={formatMessage('cookies.card.settings.form.accept')}
            marginBottom="small"
            value="true"
            {...register('cookiesAccepted')}
          />
          <RadioButton
            label={formatMessage('cookies.card.settings.form.reject')}
            value="false"
            marginBottom="medium"
            {...register('cookiesAccepted')}
          />
        </fieldset>
        <div className={styles.saveButton}>
          <Button
            type="submit"
            fullWidth
            marginBottom="large"
            {...buildActionAnalyticsAttributes('save-preferences')}
          >
            {formatMessage('cookies.card.settings.form.cta')}
          </Button>
          {prefsUpdated && (
            <Text bold color="mid-teal">
              {formatMessage('cookies.card.settings.form.success')}
            </Text>
          )}
        </div>
      </form>

      <Title is="h2" marginBottom="small">
        {formatMessage('cookies.card.other.title')}
      </Title>
      {formatMessage('cookies.card.other.description')
        .split('\n')
        .map((p, i) => (
          <Text key={i} marginBottom="small">
            {p}
          </Text>
        ))}

      <Title is="h2">{formatMessage('cookies.card.questions.title')}</Title>
      <Text marginTop="extra-small">
        {formatMessage('cookies.card.questions.description')}
        <a className={styles.link} href="mailto:help@monzo.com">
          help@monzo.com
        </a>
        .
      </Text>
    </PlainLayout>
  )
}

CookiesPage.analytics = { name: 'cookie-notice' }
